// URL decode lookup map for common characters (cached outside function for performance)
const URL_DECODE_MAP: Record<string, string> = {
  '%20': ' ',  // space
  '%21': '!',  // exclamation
  '%22': '"',  // double quote
  '%23': '#',  // hash
  '%24': '$',  // dollar
  '%25': '%',  // percent
  '%26': '&',  // ampersand
  '%27': "'",  // single quote
  '%28': '(',  // left parenthesis
  '%29': ')',  // right parenthesis
  '%2A': '*',  // asterisk
  '%2B': '+',  // plus
  '%2C': ',',  // comma
  '%2D': '-',  // hyphen
  '%2E': '.',  // period
  '%2F': '/',  // forward slash
  '%3A': ':',  // colon
  '%3B': ';',  // semicolon
  '%3C': '<',  // less than
  '%3D': '=',  // equals
  '%3E': '>',  // greater than
  '%3F': '?',  // question mark
  '%40': '@',  // at symbol
  '%5B': '[',  // left bracket
  '%5C': '\\', // backslash
  '%5D': ']',  // right bracket
  '%5E': '^',  // caret
  '%5F': '_',  // underscore
  '%60': '`',  // backtick
  '%7B': '{',  // left brace
  '%7C': '|',  // pipe
  '%7D': '}',  // right brace
  '%7E': '~'   // tilde
};

// Single regex to match all URL-encoded characters (cached for performance)
const URL_ENCODED_REGEX = /%[0-9A-Fa-f]{2}/g;

/**
 * Cleans and decodes URL-encoded values while removing quotes.
 *
 * This function handles comprehensive URL decoding for all characters, not just quotes.
 * It attempts to decode the entire string first, then removes any remaining quotes.
 * If decoding fails, it falls back to optimized manual decoding using a lookup map.
 *
 * @param value - The string value to clean and decode
 * @returns The cleaned and decoded string, or null if the input is empty/null
 */
export const cleanUrlEncodedValue = (value: string | null): string | null => {
  if (!value) return null;

  try {
    // Try to decode URL-encoded string comprehensively
    const decoded = decodeURIComponent(value);
    // Remove any remaining quotes (both regular and various quote types)
    const cleaned = decoded.trim().replace(/^["']|["']$/g, '');
    return cleaned || null;
  } catch (error) {
    // If decoding fails, fall back to optimized manual decoding
    try {
      // Decode using cached lookup map with single regex replace
      let cleaned = value.trim().replace(URL_ENCODED_REGEX, (match) => {
        return URL_DECODE_MAP[match.toUpperCase()] || match;
      });
      
      // Remove quotes after manual decoding
      cleaned = cleaned.replace(/^["']|["']$/g, '');
      return cleaned || null;
    } catch (fallbackError) {
      // Final fallback - just remove quotes without decoding
      const cleaned = value.trim().replace(/^["']|["']$/g, '');
      return cleaned || null;
    }
  }
};

export default cleanUrlEncodedValue;
